import EventCard from '../components/EventCard';
import OfflinePill from '../components/OfflinePill';
import PhotoTile from '../components/PhotoTile';
import Wardrobe from '../components/Wardrobe';
import { SkeletonEvents } from '../components/Skeleton';
import { useScheduleModel } from '../lib/schedule';
import { useIsDesktop } from '../lib/viewport';
import { useMedia, visibleTo } from '../lib/media';
import { Link } from 'react-router-dom';

/**
 * Artboards 1d (phone) and 1l (laptop).
 * Same components either way: on a phone the days stack under sticky headers;
 * on a laptop the planner and the newest photos move into a second column and
 * the event rows grow a time gutter. Nothing is added or removed between them.
 */
export default function Schedule() {
  const model = useScheduleModel();
  const desktop = useIsDesktop();
  const media = useMedia();
  const recent = visibleTo(media).slice(0, desktop ? 4 : 0);

  if (model.loading) {
    return (
      <div className="page">
        <div className="day-head"><h4>Loading the week…</h4></div>
        <SkeletonEvents />
      </div>
    );
  }

  if (model.error && model.events.length === 0) {
    return (
      <div className="page empty">
        <div className="mark">♦</div>
        <h4 style={{ margin: 0 }}>The schedule didn't load</h4>
        <p>The connection dropped before we got it. It is only a small file — try once more.</p>
        <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>Try again</button>
      </div>
    );
  }

  const days = (
    <>
      {model.days.map((day) => (
        <section key={day.key}>
          <div className="day-head">
            <h4>{desktop ? day.longLabel : day.label}</h4>
            {day.isToday && <span className="day-note">Today</span>}
          </div>
          <div className="ev-list">
            {day.events.map((event) => {
              const state = model.stateOf(event);
              return (
                <EventCard
                  key={event.id}
                  event={event}
                  venue={model.venueOf(event.venueId)}
                  state={state}
                  at={model.at}
                  density={state === 'now' || state === 'next' ? 'hero' : 'row'}
                />
              );
            })}
          </div>
        </section>
      ))}
      <div className="tz-note">All times Khao Lak, GMT+7</div>
    </>
  );

  if (desktop) {
    return (
      <div className="cols">
        <div>
          <OfflinePill fetchedAt={model.fetchedAt} />
          {days}
        </div>
        <aside className="col-side">
          {model.wardrobe && <Wardrobe content={model.wardrobe} full />}
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '26px 0 12px' }}>
            <h4 style={{ margin: 0 }}>Coming in</h4>
            <span style={{ fontSize: 12, opacity: 0.55 }}>{visibleTo(media).length} photos so far</span>
          </div>
          <div className="gal-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            {recent.map((item) => <PhotoTile key={item.id} item={item} />)}
          </div>
          <Link className="btn btn-secondary btn-block" style={{ minHeight: 46, marginTop: 12 }} to="/gallery">
            Open the gallery
          </Link>
        </aside>
      </div>
    );
  }

  return (
    <div className="page">
      <OfflinePill fetchedAt={model.fetchedAt} />
      {model.wardrobe && (
        <div style={{ padding: '12px 14px 0' }}>
          <Wardrobe content={model.wardrobe} />
        </div>
      )}
      {days}
    </div>
  );
}
